import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, X, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateTicket = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calculationMode, setCalculationMode] = useState('auto'); // 'auto' or 'manual'
  const [formData, setFormData] = useState({
    customer_id: '',
    pickup_date: '',
    delivery_date: '',
    kg: '',
    clothes_count: '',
    service_type: '',
    special_instructions: '',
    items: [],
    total_price: 0
  });
  const [newItem, setNewItem] = useState({
    item_name: '',
    service_type: '',
    quantity: 1,
    price_per_unit: 0
  });

  useEffect(() => {
    fetchCustomers();
    fetchRates();
  }, []);

  // FIX: Separate effects to prevent infinite loops
  // Auto-calculate when KG or service type changes (only in auto mode)
  useEffect(() => {
    if (calculationMode === 'auto' && formData.kg && formData.service_type && rates.length > 0) {
      const kgRate = rates.find(r => {
        const unitMatch = r.unit && r.unit.toLowerCase().trim() === 'kg';
        const serviceMatch = r.service_type && 
          r.service_type.toLowerCase().trim() === formData.service_type.toLowerCase().trim();
        return unitMatch && serviceMatch;
      });
      
      if (kgRate) {
        const calculatedPrice = parseFloat(formData.kg) * parseFloat(kgRate.price_per_unit);
        setFormData(prev => ({ ...prev, total_price: calculatedPrice, items: [] }));
      } else {
        setFormData(prev => ({ ...prev, total_price: 0, items: [] }));
      }
    }
  }, [formData.kg, formData.service_type, calculationMode, rates]);

  // Calculate total from items when in manual mode (only track items length, not the whole array)
  useEffect(() => {
    if (calculationMode === 'manual' && formData.items.length > 0) {
      const total = formData.items.reduce((sum, item) => sum + item.total, 0);
      setFormData((prev) => ({ ...prev, total_price: total }));
    } else if (calculationMode === 'manual' && formData.items.length === 0) {
      setFormData((prev) => ({ ...prev, total_price: 0 }));
    }
  }, [formData.items.length, calculationMode]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to fetch customers');
    }
  };

  const fetchRates = async () => {
    try {
      const response = await axios.get(`${API}/rates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRates(response.data);
    } catch (error) {
      toast.error('Failed to fetch rates');
    }
  };

  const addItem = () => {
    if (!newItem.item_name || !newItem.service_type || newItem.quantity <= 0) {
      toast.error('Please fill all item details');
      return;
    }
    const total = newItem.quantity * newItem.price_per_unit;
    setFormData({
      ...formData,
      items: [...formData.items, { ...newItem, total }]
    });
    setNewItem({ item_name: '', service_type: '', quantity: 1, price_per_unit: 0 });
  };

  const removeItem = (index) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: updatedItems });
  };

  const handleRateSelect = (rateId) => {
    const selectedRate = rates.find((r) => r.id === rateId);
    if (selectedRate) {
      setNewItem({
        item_name: selectedRate.item_name,
        service_type: selectedRate.service_type,
        quantity: 1,
        price_per_unit: selectedRate.price_per_unit
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    
    // In auto mode, items are not required
    if (calculationMode === 'manual' && formData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    // In auto mode, ensure we have kg and service type
    if (calculationMode === 'auto' && (!formData.kg || !formData.service_type)) {
      toast.error('Please enter KG and select service type');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/tickets`,
        {
          ...formData,
          pickup_date: new Date(formData.pickup_date).toISOString(),
          delivery_date: formData.delivery_date ? new Date(formData.delivery_date).toISOString() : null,
          kg: parseFloat(formData.kg),
          clothes_count: parseInt(formData.clothes_count)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ticket created successfully');
      navigate(`/tickets/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <Button variant="ghost" onClick={() => navigate('/tickets')} data-testid="back-btn">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Tickets
      </Button>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Customer *</Label>
                <Select value={formData.customer_id} onValueChange={(value) => setFormData({ ...formData, customer_id: value })}>
                  <SelectTrigger data-testid="customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Service Type *</Label>
                <Select value={formData.service_type} onValueChange={(value) => setFormData({ ...formData, service_type: value })}>
                  <SelectTrigger data-testid="service-type-select">
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="wash">Wash</SelectItem>
                    <SelectItem value="dry-clean">Dry Clean</SelectItem>
                    <SelectItem value="iron">Iron</SelectItem>
                    <SelectItem value="wash-iron">Wash + Iron</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pickup-date">Pickup Date *</Label>
                <Input
                  id="pickup-date"
                  type="date"
                  value={formData.pickup_date}
                  onChange={(e) => setFormData({ ...formData, pickup_date: e.target.value })}
                  required
                  data-testid="pickup-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="delivery-date">Delivery Date</Label>
                <Input
                  id="delivery-date"
                  type="date"
                  value={formData.delivery_date}
                  onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })}
                  data-testid="delivery-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="kg">Weight (KG) *</Label>
                <Input
                  id="kg"
                  type="number"
                  step="0.1"
                  value={formData.kg}
                  onChange={(e) => setFormData({ ...formData, kg: e.target.value })}
                  required
                  data-testid="kg-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="clothes-count">Total Clothes *</Label>
                <Input
                  id="clothes-count"
                  type="number"
                  value={formData.clothes_count}
                  onChange={(e) => setFormData({ ...formData, clothes_count: e.target.value })}
                  required
                  data-testid="clothes-count-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="special-instructions">Special Instructions</Label>
              <Textarea
                id="special-instructions"
                value={formData.special_instructions}
                onChange={(e) => setFormData({ ...formData, special_instructions: e.target.value })}
                rows={3}
                data-testid="special-instructions-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Calculation Mode Toggle */}
        <Card className="bg-indigo-50 border-indigo-200">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex-1">
                <p className="font-medium text-indigo-900">Pricing Mode</p>
                <p className="text-sm text-indigo-600">
                  {calculationMode === 'auto' 
                    ? 'Auto: Price calculated from KG × Rate per KG' 
                    : 'Manual: Add itemized details below'}
                </p>
                {calculationMode === 'auto' && (
                  <div className="mt-2">
                    <p className="text-xs text-indigo-700 font-medium">Available KG Rates:</p>
                    {rates.filter(r => r.unit && r.unit.toLowerCase() === 'kg').length > 0 ? (
                      <ul className="text-xs text-indigo-600 mt-1 space-y-1">
                        {rates.filter(r => r.unit && r.unit.toLowerCase() === 'kg').map(r => (
                          <li key={r.id}>• {r.service_type}: ₹{r.price_per_unit}/kg</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-orange-600 mt-1">⚠️ No KG rates found. Add rates in Rate Management first!</p>
                    )}
                  </div>
                )}
              </div>
              <Button
                type="button"
                variant={calculationMode === 'auto' ? 'default' : 'outline'}
                onClick={() => setCalculationMode(calculationMode === 'auto' ? 'manual' : 'auto')}
                data-testid="toggle-calculation-mode"
                className="whitespace-nowrap"
              >
                {calculationMode === 'auto' ? 'Switch to Manual' : 'Switch to Auto'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Items (only show in manual mode) */}
        {calculationMode === 'manual' && (
        <Card>
          <CardHeader>
            <CardTitle>Items & Pricing</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Add Item Form */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                <div className="md:col-span-2">
                  <Label>Quick Select from Rate List</Label>
                  <Select onValueChange={handleRateSelect}>
                    <SelectTrigger data-testid="rate-select">
                      <SelectValue placeholder="Select from rates" />
                    </SelectTrigger>
                    <SelectContent>
                      {rates.map((rate) => (
                        <SelectItem key={rate.id} value={rate.id}>
                          {rate.item_name} - {rate.service_type} (₹{rate.price_per_unit})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                <div>
                  <Label>Item Name</Label>
                  <Input
                    value={newItem.item_name}
                    onChange={(e) => setNewItem({ ...newItem, item_name: e.target.value })}
                    placeholder="e.g., Shirt"
                    data-testid="item-name-input"
                  />
                </div>
                <div>
                  <Label>Service</Label>
                  <Input
                    value={newItem.service_type}
                    onChange={(e) => setNewItem({ ...newItem, service_type: e.target.value })}
                    placeholder="e.g., wash"
                    data-testid="item-service-input"
                  />
                </div>
                <div>
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    value={newItem.quantity}
                    onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}
                    min="1"
                    data-testid="item-quantity-input"
                  />
                </div>
                <div>
                  <Label>Price/Unit</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newItem.price_per_unit}
                    onChange={(e) => setNewItem({ ...newItem, price_per_unit: parseFloat(e.target.value) || 0 })}
                    data-testid="item-price-input"
                  />
                </div>
                <div className="flex items-end">
                  <Button type="button" onClick={addItem} className="w-full" data-testid="add-item-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add
                  </Button>
                </div>
              </div>
            </div>

            {/* Items List */}
            {formData.items.length > 0 && (
              <div className="space-y-2">
                <Label>Added Items</Label>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Item</th>
                        <th className="text-left p-3 text-sm font-medium text-slate-600">Service</th>
                        <th className="text-center p-3 text-sm font-medium text-slate-600">Qty</th>
                        <th className="text-right p-3 text-sm font-medium text-slate-600">Price/Unit</th>
                        <th className="text-right p-3 text-sm font-medium text-slate-600">Total</th>
                        <th className="w-12"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.items.map((item, index) => (
                        <tr key={index} className="border-t border-slate-200">
                          <td className="p-3 text-sm">{item.item_name}</td>
                          <td className="p-3 text-sm">{item.service_type}</td>
                          <td className="p-3 text-sm text-center">{item.quantity}</td>
                          <td className="p-3 text-sm text-right">₹{item.price_per_unit.toFixed(2)}</td>
                          <td className="p-3 text-sm text-right font-medium">₹{item.total.toFixed(2)}</td>
                          <td className="p-3">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItem(index)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`remove-item-${index}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end p-4 bg-slate-50 rounded-lg">
                  <div className="text-right">
                    <p className="text-sm text-slate-600 mb-1">Total Amount</p>
                    <p className="text-3xl font-bold text-indigo-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      ₹{formData.total_price.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Total Display (shown in both modes) */}
        <Card>
          <CardContent className="p-6">
            <div className="flex justify-end">
              <div className="text-right">
                <p className="text-sm text-slate-600 mb-1">Total Amount</p>
                <p className="text-4xl font-bold text-indigo-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  ₹{formData.total_price.toFixed(2)}
                </p>
                {calculationMode === 'auto' && formData.kg && formData.service_type && (
                  <p className="text-xs text-slate-500 mt-2">
                    {formData.kg} kg × Rate per KG ({formData.service_type})
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/tickets')} data-testid="cancel-btn">
            Cancel
          </Button>
          <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={loading} data-testid="create-ticket-submit-btn">
            {loading ? 'Creating...' : 'Create Ticket'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateTicket;
